(ns ui.editor2.sidebar-emojies
  "The editor2 Emoji sidebar: a search field, a bar of category tabs and
   a scroll area with the emojis of `ui.editor2.emoji-catalog`. The
   Storrito stickers are shown as larger tiles, the emojis as a dense
   grid. A click adds the emoji's image as a clip. Its state lives under
   `[:editor2 :emoji-sidebar]` (`:query`, `:category`)."
  (:require [ui.editor2.emoji-catalog :as catalog]))

(defn icon [selected?]
  [:svg.size-6
   {:xmlns "http://www.w3.org/2000/svg"
    :fill "none"
    :viewBox "0 0 24 24"
    :stroke-width "1.5"
    :stroke "currentColor"
    :class ["size-6" (when-not selected? "text-slate-400")]}
   [:path
    {:stroke-linecap "round"
     :stroke-linejoin "round"
     :d "M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"}]]
  )

(defn entry
  [selected?]
  [:div
   {:class ["flex" "pt-3" "pb-3" "pl-2" "pr-2" "flex-col" "justify-center" "items-center" "gap-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-600"
            (when selected?
              "bg-gray-800")]
    :on {:click [[:store/assoc-in [:editor2 :sidebar-panel] :emojies]]}}
   (icon selected?)
   [:span
    {:class ["text-center" "text-xs" "font-medium" (when-not selected? "text-slate-400")]}
    "Emojis"]])

(def ^:private query-path
  [:editor2 :emoji-sidebar :query])

(def ^:private category-path
  [:editor2 :emoji-sidebar :category])

(def ^:private focus-ring
  ["focus-visible:outline-none" "focus-visible:ring-2" "focus-visible:ring-storrito-400"])

(def ^:private category-icons
  "The image of each category tab."
  {"custom" "/emojicons/custom/standard/emoji_instagram_love.png"
   "people" (catalog/noto-uri "1f600")
   "nature" (catalog/noto-uri "1f43b")
   "foods" (catalog/noto-uri "1f354")
   "activity" (catalog/noto-uri "26bd")
   "places" (catalog/noto-uri "1f697")
   "objects" (catalog/noto-uri "1f4a1")
   "symbols" (catalog/noto-uri "2764")})

(def ^:private search-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg"
    :viewBox "0 0 20 20"
    :fill "currentColor"
    :aria-hidden "true"
    :class ["size-4"]}
   [:path
    {:fill-rule "evenodd"
     :clip-rule "evenodd"
     :d "M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"}]])

(def ^:private clear-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg"
    :viewBox "0 0 20 20"
    :fill "currentColor"
    :aria-hidden "true"
    :class ["size-4"]}
   [:path
    {:d "M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"}]])

(defn search-field
  [query]
  [:div
   {:class ["relative" "w-full"]}
   [:span
    {:class ["pointer-events-none" "absolute" "inset-y-0" "left-0" "flex" "items-center" "pl-3" "text-slate-400"]}
    search-icon]
   [:input
    {:type "text"
     :inputmode "search"
     :enterkeyhint "search"
     :autocomplete "off"
     :spellcheck "false"
     :aria-label "Search emojis"
     :placeholder "Search emojis"
     :value (or query "")
     :class ["block" "w-full" "h-10" "rounded-lg" "border" "border-gray-700" "bg-gray-900" "pl-9" "pr-9" "text-sm" "text-white" "placeholder:text-slate-500" "transition-colors" "hover:border-gray-600" "focus:border-storrito-400" "focus:outline-none" "focus:ring-1" "focus:ring-storrito-400"]
     :on {:input [[:store/assoc-in query-path :event/target.value]]}}]
   (when (seq query)
     [:button
      {:type "button"
       :title "Clear search"
       :aria-label "Clear search"
       :class (into ["absolute" "inset-y-0" "right-0" "flex" "w-9" "items-center" "justify-center" "rounded-r-lg" "text-slate-400" "hover:text-white"]
                    focus-ring)
       :on {:click [[:store/assoc-in query-path ""]]}}
      clear-icon])])

(defn category-tab
  "A tab of the category bar. While a search is shown no tab is
   selected; a click on a tab ends the search."
  [selected-id {:keys [id label]}]
  (let [selected? (= id selected-id)]
    [:button
     {:replicant/key id
      :type "button"
      :aria-pressed (str selected?)
      :aria-label label
      :title label
      :class (-> ["group" "flex" "h-8" "min-w-0" "items-center" "justify-center" "rounded-md" "transition-colors"]
                 (into focus-ring)
                 (conj (if selected?
                         "bg-gray-700"
                         "hover:bg-gray-800")))
      :on {:click [[:store/assoc-in query-path ""]
                   [:store/assoc-in category-path id]]}}
     [:img
      {:src (category-icons id)
       :alt ""
       :draggable false
       :class (cond-> ["size-5" "object-contain" "pointer-events-none" "transition"]
                (not selected?)
                ;; Muted instead of grayscale: a gray red heart would be
                ;; almost invisible on the dark bar.
                (into ["saturate-50" "opacity-60" "group-hover:saturate-100" "group-hover:opacity-100"]))}]]))

(defn category-bar
  [selected-id]
  [:div
   {:role "group"
    :aria-label "Emoji categories"
    :class ["grid" "w-full" "grid-cols-8" "gap-0.5" "rounded-lg" "bg-gray-900" "p-1"]}
   (map (partial category-tab selected-id) catalog/categories)])

(defn emoji-button
  "A cell of the emoji grid."
  [{:keys [id label uri]}]
  [:button
   {:replicant/key id
    :type "button"
    :title label
    :aria-label (str "Add " label)
    :class (into ["group" "flex" "aspect-square" "items-center" "justify-center" "rounded-lg" "p-1.5" "transition-colors" "hover:bg-gray-700"]
                 focus-ring)
    :on {:click [[:editor2/add-emoji {:id id
                                      :uri uri}]]}}
   [:img
    {:src uri
     :alt ""
     :loading "lazy"
     :draggable false
     :class ["size-full" "object-contain" "pointer-events-none" "motion-safe:transition-transform" "group-hover:scale-110"]}]])

(defn sticker-tile
  "A tile of a Storrito sticker: larger than an emoji cell, since many
   of them are badges with text."
  [{:keys [id label uri]}]
  [:button
   {:replicant/key id
    :type "button"
    :title label
    :aria-label (str "Add " label)
    :class (into ["group" "flex" "aspect-square" "items-center" "justify-center" "rounded-xl" "bg-gray-900/60" "p-2.5" "ring-1" "ring-inset" "ring-white/5" "transition-colors" "hover:bg-gray-700"]
                 focus-ring)
    :on {:click [[:editor2/add-emoji {:id id
                                      :uri uri}]]}}
   [:img
    {:src uri
     :alt ""
     :loading "lazy"
     :draggable false
     :class ["size-full" "object-contain" "pointer-events-none" "motion-safe:transition-transform" "group-hover:scale-105"]}]])

(defn section
  "A titled block of the scroll area: `:tiles?` shows Storrito stickers."
  [{:keys [title tiles? emojis]}]
  [:section
   {:replicant/key title
    :class ["flex" "flex-col" "gap-2"]}
   (when title
     [:h4
      {:class ["text-[11px]" "font-semibold" "uppercase" "tracking-wider" "text-slate-400"]}
      title])
   (if tiles?
     [:div
      {:class ["grid" "grid-cols-3" "gap-2"]}
      (map sticker-tile emojis)]
     [:div
      {:class ["grid" "grid-cols-6" "gap-0.5"]}
      (map emoji-button emojis)])])

(defn empty-state
  [query]
  [:div
   {:class ["flex" "flex-col" "items-center" "gap-2" "px-2" "py-12" "text-center"]}
   [:img
    {:src (catalog/noto-uri "1f50d")
     :alt ""
     :class ["size-10" "opacity-60" "grayscale"]}]
   [:p
    {:class ["text-sm" "font-medium" "text-slate-200"]}
    (str "No emojis for “" (catalog/normalize-query query) "”")]
   [:p
    {:class ["text-xs" "text-slate-400"]}
    "Try a single word like smile, heart or party."]
   [:button
    {:type "button"
     :class (into ["mt-2" "rounded-md" "px-3" "py-1.5" "text-xs" "font-medium" "text-white" "ring-1" "ring-inset" "ring-gray-600" "transition-colors" "hover:bg-gray-700"]
                  focus-ring)
     :on {:click [[:store/assoc-in query-path ""]]}}
    "Clear search"]])

(defn sections
  "The sections of the scroll area for a search (`results`) or a
   category."
  [results {:keys [id groups emojis]}]
  (cond
    results
    (let [stickers (filter :sticker? results)
          others (remove :sticker? results)]
      (cond-> []
        (seq stickers) (conj {:title "Storrito stickers"
                              :tiles? true
                              :emojis stickers})
        (seq others) (conj {:title (when (seq stickers)
                                     "Emojis")
                            :emojis others})))

    (= "custom" id)
    (map (fn [{:keys [label emojis]}]
           {:title label
            :tiles? true
            :emojis emojis})
         groups)

    :else
    [{:emojis emojis}]))

(def ^:private content
  "The heading and the scroll area of the category or the search results.
   editor2 renders on every store change (60 times per second during
   playback); remembering the last hiccup gives Replicant the identical
   value, so it skips the up to 460 emoji buttons."
  (catalog/memoize-last
    (fn [category-id query]
      (let [results (catalog/search query)
            {:keys [label emojis] :as category} (catalog/category category-id)]
        [:div
         {:class ["flex" "min-h-0" "w-full" "flex-1" "flex-col" "gap-2"]}
         [:div
          {:class ["flex" "items-baseline" "justify-between" "gap-2"]}
          [:h3
           {:class ["truncate" "text-xs" "font-semibold" "text-slate-200"]}
           (if results
             (str "Results for “" (catalog/normalize-query query) "”")
             label)]
          [:span
           {:class ["shrink-0" "text-xs" "tabular-nums" "text-slate-500"]}
           (count (or results emojis))]]
         [:div
          ;; A new scroll area per category and search starts at the top.
          {:replicant/key (str category-id "/" (catalog/normalize-query query))
           :class ["min-h-0" "flex-1" "overflow-y-auto" "overscroll-contain" "-mr-4" "pr-2"
                   "[scrollbar-gutter:stable]" "[scrollbar-width:thin]" "[scrollbar-color:#4b5563_transparent]"
                   ;; Fades out the last row that is cut off, a hint
                   ;; that the area scrolls.
                   "[mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)]"]}
          (if (and results (empty? results))
            (empty-state query)
            [:div
             {:class ["flex" "flex-col" "gap-4" "pb-6"]}
             (map section (sections results category))])]]))))

(defn panel
  [state]
  (let [{:keys [query category]} (get-in state [:editor2 :emoji-sidebar])
        category-id (:id (catalog/category category))]
    [:div
     {:replicant/key :emoji-panel
      :class ["flex" "min-h-0" "w-full" "flex-1" "flex-col" "gap-3"]}
     (search-field query)
     (category-bar (when-not (catalog/normalize-query query)
                     category-id))
     (content category-id query)]))

(defn get-image-dimensions [url]
  (let [img (.createElement js/document "img")]
    (set! (.-src img) url)
    (js/Promise.
      (fn [resolve reject]
        (set! (.-onload img)
              (fn []
                (let [width  (.-naturalWidth img)
                      height (.-naturalHeight img)]
                  (js/URL.revokeObjectURL url)
                  (resolve {:width width :height height}))))
        (set! (.-onerror img)
              (fn [_e]
                ;; Reject with an Error, not with the DOM event: the event
                ;; carries no message, so Sentry grouped every failed
                ;; image as "Event captured as promise rejection"
                ;; (STORRITO-V2-5W).
                (reject (js/Error. (str "Failed to load image: " url)))))))))

(defn add-emoji!
  "Adds the emoji of the action `[_ {:keys [id uri]}]` as an image clip."
  [{:keys [store action]}]
  (let [[_ {:keys [id uri]}] action
        path (str (.-origin js/location) uri)]
    (-> (get-image-dimensions path)
        (.then (fn [{:keys [width height]}]
                 (let [asset-uuid (random-uuid)
                       asset [{:db/id "emoji-asset"
                               :asset/uuid asset-uuid
                               :asset/type :image
                               :asset/uri path
                               :asset/name (str "emoji-" id)
                               :asset/width width
                               :asset/height height}]
                       dispatch! (:replicant/dispatch! @store)]
                   (dispatch! {:actions [[:editor2/transact-asset asset]
                                         [:editor2/prepare-assets!]
                                         [:editor2/add-asset-as-clip asset-uuid]]}))))
        ;; An emoji whose image cannot be loaded adds no clip. Logged
        ;; instead of escaping as an unhandled rejection.
        (.catch (fn [error]
                  (js/console.warn "Emoji not added:" error))))))

(def register
  [{:ui.action/kind :editor2/add-emoji
    :ui.action/handler add-emoji!}])
